import { useMemo } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import {
  AssetCustomFieldType as AssetCustomFieldTypeEnum,
  type AssetCustomFieldType,
} from '@evimo/types/enums/asset-custom-field-type';

const { Text } = Typography;

export function getUnitFromRules(rules: object | null): string | null {
  if (!rules || typeof rules !== 'object' || Array.isArray(rules)) {
    return null;
  }

  const maybeUnit = (rules as { unit?: unknown }).unit;
  if (typeof maybeUnit !== 'string') {
    return null;
  }

  const normalized = maybeUnit.trim();
  return normalized.length > 0 ? normalized : null;
}

type UseAssetCustomFieldColumnsParams = {
  t: (key: string, fallback: string) => string;
  onEdit: (field: FindAssetCustomFieldResponseDto) => void;
  onDelete: (field: FindAssetCustomFieldResponseDto) => void;
};

export function useAssetCustomFieldColumns({
  t,
  onEdit,
  onDelete,
}: UseAssetCustomFieldColumnsParams): TableProps<FindAssetCustomFieldResponseDto>['columns'] {
  return useMemo(
    () => [
      {
        title: t('table.fields.name', 'Name'),
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: t('table.fields.fieldKey', 'Field Key'),
        dataIndex: 'fieldKey',
        key: 'fieldKey',
        render: (value: string) => (
          <Text code className="app-asset-custom-field-key-code">
            {value}
          </Text>
        ),
      },
      {
        title: t('table.fields.fieldType', 'Type'),
        dataIndex: 'fieldType',
        key: 'fieldType',
        onFilter: (value, record) => record.fieldType === value,
        render: (value: AssetCustomFieldType) => (
          <Tag className="app-asset-custom-field-type-tag">{value}</Tag>
        ),
      },
      {
        title: t('table.fields.required', 'Required'),
        dataIndex: 'required',
        key: 'required',
        width: 120,
        filters: [
          { text: t('table.requiredYes', 'Yes'), value: true },
          { text: t('table.requiredNo', 'No'), value: false },
        ],
        onFilter: (value, record) => record.required === value,
        render: (value: boolean) =>
          value ? (
            <Tag color="green">{t('table.requiredYes', 'Yes')}</Tag>
          ) : (
            <Tag color="default">{t('table.requiredNo', 'No')}</Tag>
          ),
      },
      {
        title: t('table.fields.unit', 'Unit'),
        dataIndex: 'rules',
        key: 'unit',
        width: 120,
        render: (rules: object | null, record) => {
          if (record.fieldType !== AssetCustomFieldTypeEnum.NUMBER) {
            return <Text type="secondary">{t('table.none', '—')}</Text>;
          }

          const unit = getUnitFromRules(rules);
          return unit ? (
            <Tag color="geekblue" className="app-asset-custom-field-unit-tag">
              {unit}
            </Tag>
          ) : (
            <Text type="secondary">{t('table.none', '—')}</Text>
          );
        },
      },
      {
        title: t('table.fields.rules', 'Rules'),
        dataIndex: 'rules',
        key: 'rules',
        ellipsis: true,
        render: (value: object | null) =>
          value ? (
            <Tooltip title={JSON.stringify(value, null, 2)}>
              <Text code ellipsis className="app-asset-custom-field-rules-code">
                {JSON.stringify(value)}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">{t('table.none', '—')}</Text>
          ),
      },
      {
        title: t('table.fields.actions', 'Actions'),
        key: 'actions',
        width: 100,
        render: (_, record) => (
          <TableRowActionsMenu
            items={[
              {
                key: 'edit',
                label: t('actions.edit', 'Edit'),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                label: t('actions.delete', 'Delete'),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [onDelete, onEdit, t],
  );
}
